import { useCallback } from 'react';
import { Box } from '@interstate/components/Box';
import { CheckBox } from '@interstate/components/CheckBox';
import { Typography } from '@interstate/components/Typography';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import type { InterstateOnChangeCallback } from '@interstate/components/InterstateEvents';
import type { CheckBoxEventValue } from '@interstate/components/CheckBox';
import type { PdfDealTermGridData, PdfTermGridItem, PDfPaymentGrid } from '@makemydeal/dr-platform-types';
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import type { ScopeGridSelection } from './usePrintGridSelection';

export interface PrintGridControlsProps {
    scopeId: string;
    gridData: PDfPaymentGrid;
    selection: ScopeGridSelection;
    disabled?: boolean;
    onToggleTerm: (scopeId: string, index: number) => void;
    onToggleDownPayment: (scopeId: string, index: number) => void;
}

const formatRate = (rate: number | undefined, rateType: string | undefined): string => {
    if (rate == null) return '';
    if (rateType === 'MoneyFactor') return rate.toFixed(5);
    return `${parseFloat(rate.toFixed(2))}%`;
};

const formatPayment = (payment: number | undefined | null): string => {
    if (payment == null) return '--';
    return formatDollarsAndCents(payment);
};

const formatDownPayment = (amount: number): string => `$${Math.round(amount).toLocaleString('en-US')}`;

type FlexBuyGridCell = PdfTermGridItem & {
    initialPaymentDiscount: number;
    firstTermPaymentDetail: NonNullable<PdfTermGridItem['firstTermPaymentDetail']>;
    secondTermPaymentDetail: NonNullable<PdfTermGridItem['secondTermPaymentDetail']>;
};

// Mirrors the PDF template's flex buy detection (dr-services-pdf `ffbUtils.hasFlexBuyGridData`) so the
// controls and the printed grid agree on which cells carry the compound first/second-term shape.
const hasFlexBuyCellData = (cell: PdfTermGridItem | undefined): cell is FlexBuyGridCell =>
    cell?.initialPaymentDiscount != null && cell.firstTermPaymentDetail != null && cell.secondTermPaymentDetail != null;

const isFlexBuyGrid = (cells: PdfDealTermGridData | undefined): boolean =>
    Boolean(cells?.some((row) => row?.some(hasFlexBuyCellData)));

// The flex buy percent is a column-level value carried on every cell, so take it from the first row
// that has payments - a down payment with no available payment leaves its cells empty.
const getInitialPaymentDiscount = (cells: PdfDealTermGridData | undefined, colIdx: number): number =>
    cells?.find((row) => row?.[colIdx]?.initialPaymentDiscount != null)?.[colIdx]?.initialPaymentDiscount ?? 0;

const renderFlexBuyCell = (scopeId: string, rowIdx: number, colIdx: number, cell: PdfTermGridItem | undefined) => {
    if (!hasFlexBuyCellData(cell)) {
        return (
            <Typography variant="body-xxs" sx={{ textAlign: 'center' }}>
                --
            </Typography>
        );
    }

    const { firstTermPaymentDetail: firstTermDetail, secondTermPaymentDetail: secondTermDetail } = cell;
    // Same split as the printed grid: first term counts from month 1, second term continues from there
    const firstRange = `1–${firstTermDetail.term} mo`;
    const secondRange = `${firstTermDetail.term + 1}–${firstTermDetail.term + secondTermDetail.term} mo`;

    return (
        <>
            <Box
                data-testid={`flex-buy-first-term-${scopeId}-${rowIdx}-${colIdx}`}
                sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, whiteSpace: 'nowrap' }}
            >
                <Typography variant="body-xxs" color="sem.color.on-surface.muted">
                    {firstRange}
                </Typography>
                <Typography variant="strong-xxs">{formatPayment(firstTermDetail.paymentAmount)}</Typography>
            </Box>
            <Box
                data-testid={`flex-buy-second-term-${scopeId}-${rowIdx}-${colIdx}`}
                sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, whiteSpace: 'nowrap' }}
            >
                <Typography variant="body-xxs" color="sem.color.on-surface.muted">
                    {secondRange}
                </Typography>
                <Typography variant="strong-xxs">{formatPayment(secondTermDetail.paymentAmount)}</Typography>
            </Box>
        </>
    );
};

export const PrintGridControls = ({
    scopeId,
    gridData,
    selection,
    disabled = false,
    onToggleTerm,
    onToggleDownPayment
}: PrintGridControlsProps) => {
    const theme = useInterstateTheme();
    const borderColor = theme.tokens.BaseColorGray200;
    const headerBg = '#FAFAFA';
    const cellBg = theme.tokens.BaseColorWhite;
    const selectedBg = theme.tokens.BaseColorGray50;
    const selectedBorderColor = theme.tokens.SemColorPrimary950;

    const cellBase = {
        border: `1px solid ${borderColor}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 0.5,
        px: 0.75,
        minWidth: 0
    };

    /* istanbul ignore next -- defensive defaults for optional grid fields */
    const { termsOptions = [], downPaymentOptions = [], downPaymentLabel, gridData: cells } = gridData;
    const { selectedTermIndices, selectedDownPaymentIndices } = selection;

    const handleTermToggle = useCallback(
        (index: number): InterstateOnChangeCallback<CheckBoxEventValue> =>
            () => {
                onToggleTerm(scopeId, index);
            },
        [scopeId, onToggleTerm]
    );

    const handleDownPaymentToggle = useCallback(
        (index: number): InterstateOnChangeCallback<CheckBoxEventValue> =>
            () => {
                onToggleDownPayment(scopeId, index);
            },
        [scopeId, onToggleDownPayment]
    );

    const isTermChecked = (colIdx: number) => selectedTermIndices.includes(colIdx);
    const isTermDisabled = (colIdx: number) =>
        disabled || (selectedTermIndices.includes(colIdx) && selectedTermIndices.length <= 1);
    const isDpChecked = (rowIdx: number) => selectedDownPaymentIndices.includes(rowIdx);
    const isDpDisabled = (rowIdx: number) =>
        disabled || (selectedDownPaymentIndices.includes(rowIdx) && selectedDownPaymentIndices.length <= 1);
    const colCount = termsOptions.length;
    const gridTemplateColumns = `auto ${Array(colCount).fill('1fr').join(' ')}`;
    const isFlexBuy = isFlexBuyGrid(cells);

    return (
        <Box data-testid={`print-grid-controls-${scopeId}`} sx={{ mt: 1 }}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns,
                    borderRadius: 1,
                    overflow: 'hidden'
                }}
            >
                <Box
                    sx={{
                        ...cellBase,
                        gridRow: '1 / 3',
                        gridColumn: '1',
                        flexDirection: 'column',
                        backgroundColor: cellBg,
                        justifyContent: 'center'
                    }}
                >
                    <Typography variant="strong-xxs" sx={{ textAlign: 'center' }}>
                        {downPaymentLabel}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        ...cellBase,
                        gridRow: '1',
                        gridColumn: `2 / ${colCount + 2}`,
                        backgroundColor: headerBg
                    }}
                >
                    <Typography variant="strong-xxs" color="sem.color.on-surface.muted">
                        {isFlexBuy ? 'Term / Flex Buy APR' : 'Term'}
                    </Typography>
                </Box>

                {termsOptions.map((term, colIdx) => {
                    /* istanbul ignore next -- defensive optional chain */
                    const cellRate = cells?.[0]?.[colIdx]?.sellRate;
                    /* istanbul ignore next -- defensive optional chain */
                    const cellRateType = cells?.[0]?.[colIdx]?.rateType;
                    const formattedRate = formatRate(cellRate, cellRateType);
                    const initialPaymentDiscount = getInitialPaymentDiscount(cells, colIdx);
                    return (
                        <Box
                            key={`term-header-${colIdx}`}
                            sx={{
                                ...cellBase,
                                gridRow: '2',
                                gridColumn: `${colIdx + 2}`,
                                justifyContent: 'flex-start',
                                backgroundColor: headerBg,
                                gap: 1,
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <CheckBox
                                id={`term-cb-${scopeId}-${colIdx}`}
                                checked={isTermChecked(colIdx)}
                                disabled={isTermDisabled(colIdx)}
                                onChange={handleTermToggle(colIdx)}
                                data-testid={`term-checkbox-${scopeId}-${colIdx}`}
                                label=""
                                value=""
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                <Typography variant="strong-xxs">{term} mo</Typography>
                                {isFlexBuy ? (
                                    <Typography variant="body-xxs">{initialPaymentDiscount}%</Typography>
                                ) : (
                                    formattedRate && <Typography variant="body-xxs">@{formattedRate}</Typography>
                                )}
                            </Box>
                        </Box>
                    );
                })}

                {downPaymentOptions.map((dp, rowIdx) => {
                    const gridRow = rowIdx + 3;
                    return [
                        <Box
                            key={`dp-${rowIdx}`}
                            sx={{
                                ...cellBase,
                                gridRow,
                                gridColumn: '1',
                                justifyContent: 'flex-start',
                                backgroundColor: cellBg,
                                gap: 1,
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <CheckBox
                                id={`dp-cb-${scopeId}-${rowIdx}`}
                                checked={isDpChecked(rowIdx)}
                                disabled={isDpDisabled(rowIdx)}
                                onChange={handleDownPaymentToggle(rowIdx)}
                                data-testid={`dp-checkbox-${scopeId}-${rowIdx}`}
                                label=""
                                value=""
                            />
                            <Typography variant="strong-xxs" sx={{ whiteSpace: 'nowrap' }}>
                                {formatDownPayment(dp)}
                            </Typography>
                        </Box>,
                        ...termsOptions.map((_, colIdx) => {
                            /* istanbul ignore next -- defensive optional chain */
                            const cell = cells?.[rowIdx]?.[colIdx];
                            /* istanbul ignore next -- defensive optional chain */
                            const isHighlighted = cell?.isSelected;

                            return (
                                <Box
                                    key={`cell-${rowIdx}-${colIdx}`}
                                    data-testid={`grid-cell-${scopeId}-${rowIdx}-${colIdx}`}
                                    sx={{
                                        ...cellBase,
                                        gridRow,
                                        gridColumn: `${colIdx + 2}`,
                                        minHeight: isFlexBuy ? 48 : 32,
                                        flexDirection: 'column',
                                        backgroundColor: isHighlighted ? selectedBg : cellBg,
                                        ...(isHighlighted && {
                                            border: `2px solid ${selectedBorderColor}`
                                        })
                                    }}
                                >
                                    {isFlexBuy ? (
                                        renderFlexBuyCell(scopeId, rowIdx, colIdx, cell)
                                    ) : (
                                        /* istanbul ignore next -- defensive optional chain */
                                        <Typography variant="body-xxs" sx={{ textAlign: 'center' }}>
                                            {formatPayment(cell?.monthlyPayment)}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })
                    ];
                })}
            </Box>
        </Box>
    );
};
